import React, { useEffect, useRef, useState } from 'react';
import { getCurrentUser, signOut } from '@aws-amplify/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { getPreferredAuthType, persistAuthType } from '../../auth/authTypeStorage';
import { configureAuth } from '../../auth/authConfig';
import { withSessionLock } from '../../auth/sessionQueue';
import { getSafeReaderReturnTo } from '../../auth/readerReturn';
import { authorLoginReturnPath } from '../../services/groupInvitations';
import PublicHeader from './PublicHeader';

const sessionKeys = ['sub', 'custom:firstname', 'custom:lastname', 'custom:membershipactive', 'custom:onboardingcomplete', 'custom:subdomain', 'idToken'];
function localCancel(value) {
  const safe = getSafeReaderReturnTo(value);
  if (!safe.startsWith('/') || /^\/switchportal(?:[/?#]|$)/i.test(safe)) return '/';
  return safe;
}
function initialType(search, state) {
  const requested = new URLSearchParams(search).get('type');
  if (['author', 'reader'].includes(requested)) return requested;
  const target = typeof state?.target === 'string' ? state.target : '';
  if (/^\/readerportal(?:[/?#]|$)/.test(target)) return 'reader';
  if (/^\/authorportal(?:[/?#]|$)/.test(target)) return 'author';
  return '';
}

export default function SwitchPortal({ setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [type, setType] = useState(() => initialType(location.search, location.state));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const heading = useRef(null);
  const audience = type === 'author' ? 'Author' : 'Reader';
  const cancelPath = localCancel(params.get('from') || location.state?.from);
  useEffect(() => { heading.current?.focus(); }, []);

  async function handleSwitch(event) {
    event.preventDefault();
    if (lock.current || !type || !event.currentTarget.reportValidity()) return;
    lock.current = true; setBusy(true); setError('');
    try {
      await withSessionLock(async () => {
        // Discover the actual session while holding the pool-configuration lock.
        const first = getPreferredAuthType() === 'reader' ? 'reader' : 'author';
        let active = null;
        try {
          for (const candidate of [first, first === 'author' ? 'reader' : 'author']) {
            configureAuth(candidate);
            try { await getCurrentUser(); active = candidate; break; }
            catch (err) {
              if (!['UserUnAuthenticatedException', 'NotAuthorizedException'].includes(err?.name)) throw err;
            }
          }
          if (active && active !== type) {
            await signOut();
            sessionKeys.forEach(key => sessionStorage.removeItem(key));
            localStorage.removeItem('user');
            setUser?.(null);
          }
          configureAuth(type);
          persistAuthType(type);
        } catch (err) {
          configureAuth(active || first);
          throw err;
        }
      });
      const requested = params.get('returnTo') || location.state?.target || '';
      const returnTo = type === 'author' ? authorLoginReturnPath('?' + new URLSearchParams({ returnTo: requested })) : getSafeReaderReturnTo(requested);
      navigate('/login' + type + (returnTo ? '?' + new URLSearchParams({ returnTo }) : ''), { replace: true });
    } catch {
      setError('We couldn’t finish switching accounts. Please try again, or contact Lady Liza below for help.');
    } finally { lock.current = false; setBusy(false); }
  }

  return <div className="min-h-screen bg-bookish-cream text-bookish-royal"><PublicHeader audience={type || 'reader'} />
    <main className="account-page flex justify-center"><div className="bg-white p-6 sm:p-8 rounded-2xl shadow-[0_12px_40px_rgba(63,37,107,0.10)] w-full max-w-md border border-bookish-purple/8">
      <p className="text-sm uppercase tracking-widest mb-3">Another chapter in your Circle</p>
      <h1 ref={heading} tabIndex={-1} className="font-serif text-3xl font-bold mb-4">{type ? 'Switch to your ' + audience + ' account?' : 'Choose your next chapter'}</h1>
      <p className="mb-5">Choose the account you’d like to open. If you’re signed in to the other portal, continuing signs you out before opening your chosen account’s login page.</p>
      <form aria-label="Switch account" aria-busy={busy} onSubmit={handleSwitch}>
        <fieldset disabled={busy} className="space-y-4">
          <label className="block" htmlFor="switch-account">Account to open</label>
          <select id="switch-account" required value={type} onChange={e => { setType(e.target.value); setError(''); }} className="w-full border border-bookish-purple/15 rounded-lg px-3 py-2 min-h-[44px] focus:ring-2 focus:ring-bookish-purple/30"><option value="">Choose an account</option><option value="author">Author</option><option value="reader">Reader</option></select>
          <p role="alert" className="text-sm">{error}</p><p role="status" className="text-sm">{busy ? 'Opening your next chapter…' : ''}</p>
          <button type="submit" className="w-full bg-bookish-royal text-white rounded-lg py-3 min-h-[44px] font-semibold hover:bg-bookish-purpleAccent disabled:opacity-60">{busy ? 'Switching accounts…' : type ? 'Continue to ' + audience + ' account' : 'Continue'}</button>
          <button type="button" onClick={() => { if (!lock.current) navigate(cancelPath, { replace: true }); }} className="w-full border border-bookish-purple/30 rounded-lg py-3 min-h-[44px] disabled:opacity-60">{cancelPath === '/' ? 'Cancel and return home' : 'Cancel and go back'}</button>
        </fieldset>
      </form>
      <p className="mt-5 text-sm">Need a hand? <a className="underline" href="mailto:author@hayleyraejohnson.com">Contact Lady Liza</a>.</p>
    </div></main>
  </div>;
}
